import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, ChevronRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Top 12 countries by likely shopper traffic. Full ISO list lives in /api/account/taxonomy
// but for the wizard we keep the dropdown short + "Other".
const COUNTRIES = [
    ["US", "United States"], ["GB", "United Kingdom"], ["CA", "Canada"], ["AU", "Australia"],
    ["DE", "Germany"], ["FR", "France"], ["NL", "Netherlands"], ["ES", "Spain"],
    ["IT", "Italy"], ["JP", "Japan"], ["KR", "South Korea"], ["MX", "Mexico"],
    ["BR", "Brazil"], ["IN", "India"], ["AE", "UAE"], ["ZA", "South Africa"],
];

const Pill = ({ active, children, ...props }) => (
    <button
        type="button"
        {...props}
        className={`border-2 border-black px-3 py-1.5 font-mono text-xs uppercase tracking-widest shadow-brutal-sm transition-transform active:translate-y-[1px] ${
            active ? "bg-[#CCFF00] text-black" : "bg-white text-black hover:bg-[#FDFBF7]"
        }`}
    >{children}</button>
);

/**
 * 3-step post-signin onboarding wizard. User can submit (full) or skip (terms-only).
 * Triggered by AuthContext when `user && !user.onboarded_at`.
 */
export const OnboardingWizard = ({ onComplete }) => {
    const { user, refreshUser } = useAuth();
    const [taxonomy, setTaxonomy] = useState(null);
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState({
        first_name: "",
        last_name: "",
        pronouns: "",
        country: "US",
        birth_year: "",
        style: {
            aesthetics: [],
            fav_brands: "",
            color_prefs: [],
            budget_bands: { tops: "$30-100", bottoms: "$30-100", outerwear: "$50-200", footwear: "$50-150" },
            sustainability: [],
        },
        consents: {
            terms: false,
            marketing: false,
            analytics: false,
        },
    });

    useEffect(() => {
        api.get("/account/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
    }, []);

    const years = useMemo(() => {
        const y = new Date().getFullYear();
        const arr = [];
        for (let i = y - 14; i >= y - 90; i--) arr.push(i);
        return arr;
    }, []);

    const toggleArr = (path, value) => {
        setData((p) => {
            const arr = path === "style.aesthetics" ? p.style.aesthetics
                : path === "style.color_prefs" ? p.style.color_prefs
                : path === "style.sustainability" ? p.style.sustainability : [];
            const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
            const style = { ...p.style };
            if (path === "style.aesthetics") style.aesthetics = next;
            if (path === "style.color_prefs") style.color_prefs = next;
            if (path === "style.sustainability") style.sustainability = next;
            return { ...p, style };
        });
    };

    const submit = async () => {
        if (!data.consents.terms) {
            toast.error("Please accept the Terms to continue");
            setStep(3);
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                first_name: data.first_name,
                last_name: data.last_name,
                pronouns: data.pronouns,
                country: data.country,
                birth_year: data.birth_year ? Number(data.birth_year) : null,
                style: {
                    ...data.style,
                    fav_brands: (data.style.fav_brands || "")
                        .split(",").map((s) => s.trim()).filter(Boolean),
                },
                consents: Object.entries(data.consents).map(([k, v]) => ({
                    consent_type: k, granted: !!v,
                })),
            };
            await api.post("/account/onboard", body);
            toast.success("Welcome to Fits Me", { description: "Your account is ready." });
            const fresh = await refreshUser?.();
            // Now that we have the user's first_name on file, replay the welcome
            // moment so the navbar pill says "Welcome back, {first_name}" instead
            // of the email-prefix it showed at the initial sign-in.
            if (fresh?.first_name) {
                window.dispatchEvent(new CustomEvent("fitsme:signed-in", { detail: fresh }));
            }
            onComplete?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSubmitting(false);
        }
    };

    // Skip = accept terms only (required), defer everything else for later.
    // The user can revisit /account to fill in their style profile.
    const skip = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/account/onboard", {
                first_name: data.first_name || "",
                last_name: data.last_name || "",
                pronouns: "",
                country: data.country || "US",
                birth_year: null,
                style: { aesthetics: [], fav_brands: [], color_prefs: [], budget_bands: {}, sustainability: [] },
                consents: [{ consent_type: "terms", granted: true }],
            });
            toast.success("Skipped for now", { description: "Finish your style profile anytime in Account." });
            const fresh = await refreshUser?.();
            if (fresh?.first_name) {
                window.dispatchEvent(new CustomEvent("fitsme:signed-in", { detail: fresh }));
            }
            onComplete?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not skip");
        } finally {
            setSubmitting(false);
        }
    };

    const STEP_LABELS = ["Basics", "Style DNA", "Privacy"];

    if (!user) return null;

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" data-testid="onboarding-wizard">
            <div className="w-full max-w-2xl bg-white border-4 border-black shadow-brutal-lg my-0 sm:my-8 max-h-[calc(100vh-1rem)] overflow-y-auto">
                {/* Header / progress */}
                <div className="px-5 py-4 border-b-2 border-black bg-[#CCFF00] sticky top-0 z-10">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-widest truncate">/ Welcome · {user.email} /</p>
                            <h2 className="font-display text-2xl sm:text-3xl font-black uppercase leading-none mt-0.5">Build your fit</h2>
                        </div>
                        <button
                            type="button"
                            onClick={skip}
                            disabled={submitting}
                            data-testid="onboarding-skip"
                            className="shrink-0 border-2 border-black bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest shadow-brutal-sm hover:bg-[#FDFBF7] active:translate-y-[1px] disabled:opacity-50 inline-flex items-center gap-1"
                            title="Skip for now and finish later in Account">
                            Skip <X className="w-3 h-3" />
                        </button>
                    </div>
                    {/* Step bar with labels */}
                    <div className="mt-3 grid grid-cols-3 gap-2" aria-label="onboarding progress" data-testid="onboarding-progress">
                        {STEP_LABELS.map((label, idx) => {
                            const n = idx + 1;
                            const reached = n <= step;
                            return (
                                <div key={label} className="flex items-center gap-2 min-w-0">
                                    <span
                                        className={`inline-flex items-center justify-center w-5 h-5 border-2 border-black font-mono text-[10px] font-black ${
                                            reached ? "bg-black text-[#CCFF00]" : "bg-white text-black"
                                        }`}
                                        data-testid={`onboarding-step-indicator-${n}`}
                                    >
                                        {n}
                                    </span>
                                    <span className={`font-mono text-[10px] uppercase tracking-widest truncate ${reached ? "text-black font-black" : "text-black/50"}`}>
                                        {label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Step 1: Basics */}
                {step === 1 && (
                    <div className="p-5 space-y-4" data-testid="onboarding-step-1">
                        <p className="font-display text-xl font-black uppercase">Tell us about you</p>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-widest">First name</span>
                                <input type="text" value={data.first_name} onChange={(e) => setData((p) => ({ ...p, first_name: e.target.value }))}
                                    data-testid="onboarding-first-name"
                                    className="mt-1 w-full border-2 border-black px-2 py-2 font-mono text-sm" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-widest">Last name</span>
                                <input type="text" value={data.last_name} onChange={(e) => setData((p) => ({ ...p, last_name: e.target.value }))}
                                    className="mt-1 w-full border-2 border-black px-2 py-2 font-mono text-sm" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-widest">Pronouns (optional)</span>
                                <input type="text" placeholder="she/her, he/him, they/them…" value={data.pronouns} onChange={(e) => setData((p) => ({ ...p, pronouns: e.target.value }))}
                                    className="mt-1 w-full border-2 border-black px-2 py-2 font-mono text-sm" />
                            </label>
                            <label className="block">
                                <span className="font-mono text-[10px] uppercase tracking-widest">Country</span>
                                <select value={data.country} onChange={(e) => setData((p) => ({ ...p, country: e.target.value }))}
                                    data-testid="onboarding-country"
                                    className="mt-1 w-full border-2 border-black px-2 py-2 font-mono text-sm bg-white">
                                    {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                                    <option value="OTHER">Other</option>
                                </select>
                            </label>
                            <label className="block col-span-2">
                                <span className="font-mono text-[10px] uppercase tracking-widest">Birth year (for age-appropriate picks)</span>
                                <select value={data.birth_year} onChange={(e) => setData((p) => ({ ...p, birth_year: e.target.value }))}
                                    data-testid="onboarding-birth-year"
                                    className="mt-1 w-full border-2 border-black px-2 py-2 font-mono text-sm bg-white">
                                    <option value="">Prefer not to say</option>
                                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                        </div>
                    </div>
                )}

                {/* Step 2: Style quiz */}
                {step === 2 && taxonomy && (
                    <div className="p-5 space-y-5" data-testid="onboarding-step-2">
                        <p className="font-display text-xl font-black uppercase">Your style DNA</p>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-2">Aesthetics (pick any)</p>
                            <div className="flex flex-wrap gap-2" data-testid="onboarding-aesthetics">
                                {taxonomy.aesthetics.map((a) => (
                                    <Pill key={a} active={data.style.aesthetics.includes(a)} onClick={() => toggleArr("style.aesthetics", a)}>
                                        {a}
                                    </Pill>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-2">Favorite brands (comma-separated)</p>
                            <input type="text" placeholder="Adidas, SHEIN, Uniqlo…" value={data.style.fav_brands}
                                onChange={(e) => setData((p) => ({ ...p, style: { ...p.style, fav_brands: e.target.value } }))}
                                data-testid="onboarding-fav-brands"
                                className="w-full border-2 border-black px-2 py-2 font-mono text-sm" />
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-2">Color preferences</p>
                            <div className="flex flex-wrap gap-2">
                                {taxonomy.color_prefs.map((c) => (
                                    <Pill key={c} active={data.style.color_prefs.includes(c)} onClick={() => toggleArr("style.color_prefs", c)}>{c}</Pill>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-2">Budget bands</p>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(taxonomy.budget_bands).map(([cat, bands]) => (
                                    <label key={cat} className="block">
                                        <span className="font-mono text-[10px] uppercase tracking-widest">{cat}</span>
                                        <select value={data.style.budget_bands[cat] || bands[1]}
                                            onChange={(e) => setData((p) => ({ ...p, style: { ...p.style, budget_bands: { ...p.style.budget_bands, [cat]: e.target.value } } }))}
                                            className="mt-1 w-full border-2 border-black px-2 py-1.5 font-mono text-xs bg-white">
                                            {bands.map((b) => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest mb-2">Sustainability filters</p>
                            <div className="flex flex-wrap gap-2">
                                {taxonomy.sustainability.map((s) => (
                                    <Pill key={s} active={data.style.sustainability.includes(s)} onClick={() => toggleArr("style.sustainability", s)}>{s}</Pill>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Step 3: Consents */}
                {step === 3 && (
                    <div className="p-5 space-y-4" data-testid="onboarding-step-3">
                        <p className="font-display text-xl font-black uppercase">Privacy & permissions</p>
                        <p className="font-mono text-xs text-black/70">
                            We need explicit permission to store your data. Read our{" "}
                            <Link to="/privacy" target="_blank" className="underline">Privacy Policy</Link> and{" "}
                            <Link to="/terms" target="_blank" className="underline">Terms</Link>.
                        </p>
                        {[
                            ["terms", "I accept the Terms & Privacy Policy", true],
                            ["marketing", "Send me the weekly Drop + sale alerts", false],
                            ["analytics", "Allow anonymous analytics so Fits Me improves recommendations", false],
                        ].map(([key, label, req]) => (
                            <label key={key} className="flex items-start gap-3 border-2 border-black p-3 cursor-pointer">
                                <input type="checkbox" checked={!!data.consents[key]}
                                    onChange={(e) => setData((p) => ({ ...p, consents: { ...p.consents, [key]: e.target.checked } }))}
                                    data-testid={`onboarding-consent-${key}`}
                                    className="mt-1 w-5 h-5 accent-black" />
                                <div>
                                    <span className="font-mono text-sm font-bold">{label}{req && <span className="text-[#FF00FF] ml-1">*</span>}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                )}

                {/* Footer */}
                <div className="px-5 py-4 border-t-2 border-black bg-[#FDFBF7] flex items-center justify-between gap-3 sticky bottom-0">
                    <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                        className="font-mono text-xs uppercase tracking-widest underline underline-offset-4 disabled:opacity-30 disabled:no-underline">
                        ← Back
                    </button>
                    {step < 3 ? (
                        <button onClick={() => setStep((s) => s + 1)}
                            data-testid="onboarding-next"
                            className="bg-black text-[#CCFF00] border-2 border-black px-5 py-2.5 font-display font-black uppercase tracking-widest shadow-brutal-sm inline-flex items-center gap-2">
                            Next <ChevronRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button onClick={submit} disabled={submitting || !data.consents.terms}
                            data-testid="onboarding-submit"
                            className="bg-[#CCFF00] text-black border-2 border-black px-5 py-2.5 font-display font-black uppercase tracking-widest shadow-brutal-sm inline-flex items-center gap-2 disabled:opacity-50">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Finish
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
